/**
 * Platform detection and the lazy-plugin pattern used across src/native/.
 *
 * Every Capacitor plugin is imported *dynamically*, inside a try/catch, and
 * only when running natively. Two reasons, both learned the hard way in this
 * kind of codebase:
 *
 *  1. Static imports pull the plugin's web shim into the browser bundle even
 *     though it can never run there — dead weight on a bundle that a phone
 *     downloads over gym wifi.
 *
 *  2. A plugin missing from the native build (added to package.json but the
 *     APK not yet rebuilt) throws on import. Caught here, that degrades to the
 *     web fallback; uncaught, it's a white screen on launch.
 *
 * The rule the rest of the app relies on: nothing outside this directory
 * imports `@capacitor/*`, and every function here works on both platforms.
 */

import { Capacitor } from '@capacitor/core'

import { withTimeout } from '../lib/timeout'
import { unthenable } from '../lib/unthenable'

export function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform()
  } catch {
    return false
  }
}

export function platform(): string {
  try {
    return Capacitor.getPlatform()
  } catch {
    return 'web'
  }
}

/**
 * Load a plugin, returning null instead of throwing when it isn't available.
 * Callers branch on null and fall back to a web API.
 *
 * The module and the plugin's name are separate arguments on purpose. The
 * obvious signature — `loadPlugin(async () => (await import(…)).Plugin)` —
 * puts the plugin object inside a promise, and a Capacitor plugin inside a
 * promise hangs forever: see lib/unthenable. Taking the module namespace (never
 * a thenable) and reading the property here means no call site can reintroduce
 * that, and the facade this returns keeps it safe to await downstream.
 */
export async function loadPlugin<M extends object, K extends keyof M>(
  loadModule: () => Promise<M>,
  name: K,
): Promise<M[K] | null> {
  if (!isNative()) return null
  try {
    // A chunk fetched from the WebView's own assets should be instant, but
    // "nothing awaits a native bridge without withTimeout" applies to the
    // import too — a hang here is invisible, and this file has already paid
    // for that once.
    const mod = await withTimeout(loadModule(), 5000, `import ${String(name)}`)
    return unthenable(mod[name])
  } catch (err) {
    console.warn('[native] plugin unavailable, using web fallback:', err)
    return null
  }
}
